import * as Joi from "joi";
import {
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	OneToMany,
	OneToOne,
	ManyToMany,
	JoinTable,
} from 'typeorm';
import { booleanTransformer, GenPartial } from 'typeormgen';
import { EpisodicPropESDoc } from './episodic-prop.interfaces';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicBudgetProp from '../episodic-budget-props/episodic-budget-prop.entity';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import Episodic from '../episodics/episodic.entity';
import { Indexable } from '../indexable';
import Inventory from '../inventory/inventory.entity';
import PrEpisodicPropPicture from '../pr-episodic-prop-pictures/pr-episodic-prop-picture.entity';
import PrWrapBoxEpisodicProp from '../pr-wrap-box-episodic-props/pr-wrap-box-episodic-prop.entity';
import PropCategory from '../prop-categories/prop-category.entity';
import PropScene from '../prop-scenes/prop-scene.entity';
import PropStatus from '../prop-statuses/prop-status.entity';
import PropStorageLocation from '../prop-storage-locations/prop-storage-location.entity';
import Prop from '../props/prop.entity';
import Vendor from '../vendors/vendor.entity';
import PrWrapBox from '../pr-wrap-boxes/pr-wrap-box.entity';

@Entity('episodic_props')
export default class EpisodicProp
	extends Inventory
	implements Indexable<EpisodicPropESDoc> {
	static tableName = 'episodic_props';

	static deptId = DEPARTMENTS.PR;

	static columns = [
		...Inventory.columns,
		'episodic_character_id',
		'material',
		'is_personal',
		'is_asset',
		'dimensions',
	];

	@Column('int')
	episodic_character_id: number;

	@Column('int')
	series_prop_id: number;

	@Column('text')
	material: string;

	@Column({ type: 'int', transformer: booleanTransformer })
	is_personal: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	is_asset: boolean;

	@Column('text')
	dimensions: string;

	@OneToMany((type) => Prop, (prop) => prop.episodicProp)
	props: Prop[];

	@OneToMany(
		(type) => PrEpisodicPropPicture,
		(prEpisodicPropPictures) => prEpisodicPropPictures.episodicProp
	)
	prEpisodicPropPictures: PrEpisodicPropPicture[];

	@ManyToOne((type) => Episodic, (episodic) => episodic.episodicProps)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	@ManyToOne((type) => PropCategory, (category) => category.episodicProps)
	@JoinColumn({
		name: 'category_id',
	})
	category: PropCategory;

	@ManyToOne((type) => PropStatus, (status) => status.episodicProps)
	@JoinColumn({
		name: 'status_id',
	})
	status: PropStatus;

	@ManyToOne((type) => Vendor, (vendor) => vendor.episodicProps)
	@JoinColumn({
		name: 'vendor_id',
	})
	vendor: Vendor;

	readonly department = EpisodicProp.deptId;

	@ManyToOne(
		(type) => PropStorageLocation,
		(propStorageLocation) => propStorageLocation.episodicProps
	)
	@JoinColumn({
		name: 'storage_location_id',
	})
	storageLocation: PropStorageLocation;

	@ManyToOne(
		(type) => EpisodicCharacter,
		(episodicCharacter) => episodicCharacter.episodicProps
	)
	@JoinColumn({
		name: 'episodic_character_id',
	})
	episodicCharacter: EpisodicCharacter;

	@OneToOne(
		(type) => PrWrapBoxEpisodicProp,
		(prWrapBoxEpisodicProp) => prWrapBoxEpisodicProp.episodicProp
	)
	prWrapBoxEpisodicProp: PrWrapBoxEpisodicProp;

	@ManyToMany(() => PrWrapBox, (prWrapBox) => prWrapBox.episodicProps)
	@JoinTable({
		name: 'pr_wrap_box_episodic_props',
		joinColumn: { name: 'episodic_prop_id' },
		inverseJoinColumn: { name: 'pr_wrap_box_id' },
	})
	prWrapBoxes: PrWrapBox[];

	@OneToMany((type) => PropScene, (propScene) => propScene.episodicProp)
	propScenes: PropScene[];

	@OneToMany(
		(type) => EpisodicBudgetProp,
		(epBudgetProp) => epBudgetProp.episodicProp
	)
	episodicBudgetProps: EpisodicBudgetProp[];

	static rules() {
		return {
			...Inventory.rules(),
			// overwrite text columns for incorrect lengths
			episodic_character_id: Joi.number().integer().allow(null),
			series_prop_id: Joi.number().integer().allow(null),
			material: Joi.string().max(1000).allow(null, ''),
			is_personal: Joi.boolean(),
			is_asset: Joi.boolean(),
			dimensions: Joi.string().max(1000).allow(null, ''),
			quantity_notes: Joi.string().max(1000).allow(null, ''),
			prEpisodicPropPictures: Joi
				.array()
				.items(PrEpisodicPropPicture.rules())
				.allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			episodic_character_id: this.episodic_character_id,
			series_prop_id: this.series_prop_id,
			material: this.material,
			is_personal: this.is_personal,
			is_asset: this.is_asset,
			dimensions: this.dimensions,
			department: this.department,
			prEpisodicPropPictures: this.prEpisodicPropPictures
				? this.prEpisodicPropPictures
				: undefined,
			main_photo_id: this.main_photo_id
				? this.main_photo_id
				: this.prEpisodicPropPictures && this.prEpisodicPropPictures.length > 0
				? this.prEpisodicPropPictures.find((pic) => pic.main_photo === true)
					? this.prEpisodicPropPictures.find((pic) => pic.main_photo === true)
							.picture_id
					: undefined
				: undefined,
			prWrapBoxes: this.prWrapBoxes,
			wrap_box_id: this.wrap_box_id,
			inv_wrap_box_names: this.inv_wrap_box_names
				? this.inv_wrap_box_names
				: this.prWrapBoxes && this.prWrapBoxes.length > 0
				? this.getWrapBoxNames(this.prWrapBoxes)
				: undefined,
			storageLocation: this.storageLocation,
			storage_location_name: this.storage_location_name
				? this.storage_location_name
				: this.storageLocation
				? this.storageLocation.name
				: undefined,
			category: this.category,
			category_name: this.category_name
				? this.category_name
				: this.category
				? this.category.name
				: undefined,
			status: this.status,
			status_name: this.status_name
				? this.status_name
				: this.status
				? this.status.name
				: undefined,
			vendor: this.vendor,
			vendor_name: this.vendor_name
				? this.vendor_name
				: this.vendor
				? this.vendor.name
				: undefined,
		};
	}

	toESDoc(data?: { episodicCharacter: EpisodicCharacter }) {
		let char_first_name: string, char_last_name: string, char_num: string;

		if (data) {
			const { episodicCharacter } = data;
			if (episodicCharacter === null) {
				char_first_name = null;
				char_last_name = null;
				char_num = null;
			} else {
				char_first_name = episodicCharacter && episodicCharacter.first_name;
				char_last_name = episodicCharacter && episodicCharacter.last_name;
				char_num = episodicCharacter && episodicCharacter.char_num;
			}
		}

		return {
			id: this.id,
			episodic_id: this.episodic_id,
			name: this.name,
			brand: this.brand,
			color: this.color,
			description: this.description,
			asset_num: this.asset_num,
			disposition_notes: this.disposition_notes,
			size: this.size,
			ep_char_id: this.episodic_character_id,
			char_first_name,
			char_last_name,
			char_num,
		};
	}

	assign(props?: GenPartial<EpisodicProp>) {
		super.assign(props);
		if (props) {
			if (typeof props.is_personal !== 'undefined') {
				this.is_personal = booleanTransformer.from(props.is_personal);
			}

			if (typeof props.is_asset !== 'undefined') {
				this.is_asset = booleanTransformer.from(props.is_asset);
			}
		}
	}

	constructor(props?: GenPartial<EpisodicProp>) {
		super(props);
	}
}
